import React, { useState, useEffect, useCallback } from "react";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { Modal, Button, Form, Nav, Tab, Spinner, Alert } from "react-bootstrap";
import "./WhatsAppTemplates.css";

// ─── Interfaces ────────────────────────────────────────────────

interface TemplateButton {
  type: "QUICK_REPLY" | "URL" | "PHONE_NUMBER";
  text: string;
  url?: string;
  phone_number?: string;
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  category: "MARKETING" | "UTILITY" | "AUTHENTICATION";
  language: string;
  status: "APPROVED" | "PENDING" | "REJECTED";
  header_type: "NONE" | "TEXT" | "IMAGE" | "VIDEO";
  header_text?: string;
  body: string;
  footer?: string;
  buttons: TemplateButton[];
  url_midia?: string;
  tipo_midia?: string;
  in_db?: boolean;          // já registrado na tabela `templates` do banco?
  texto_template?: string;  // versão com variáveis nomeadas ({{nome}}) salva no banco
  classificacao?: string | null; // Boas-vindas | Manual | Cadência | null(vazio)
  // Em quais WABAs do grupo este template existe (vem do backend quando waba="todas").
  // `id` é o id DAQUELA conta — template é por WABA e cada uma tem o seu.
  _wabas?: { waba_id: string; status: string; id?: string }[];
}

// Uma WABA do grupo e os números que pertencem a ela. Um grupo pode ter várias — o
// template vive numa WABA específica, então criar/listar é sempre por WABA.
interface WabaOption {
  waba_id: string;
  instancias: { ins_nome: string; ins_numero: string }[];
}

const TODAS = "todas";

// Resposta de /api/whatsapp/templates/ajustar_utility
interface IaResultado {
  convertivel: boolean;
  veredito?: string;
  caso_de_uso?: string | null;
  diagnostico: { trecho: string; gatilho: string }[];
  header?: string | null;
  body?: string | null;
  footer?: string | null;
  botoes: string[];
  exemplos_body: string[];
  variaveis_novas?: string | null;
  risco?: string;
  alternativa?: string | null;
  avisos: string[];
}

// O que estava no form antes do ajuste da IA (para "Desfazer")
interface IaSnapshot {
  category: WhatsAppTemplate["category"];
  headerText: string;
  body: string;
  footer: string;
  buttons: TemplateButton[];
  bodyExamples: string[];
}

// Campo de card disponível para nomear variáveis (label exibido no card-builder)
interface CardFieldOption {
  group: string;
  label: string;
}

interface OfficialConfig {
  waba_id: string;
  phone_id: string;
  official_token: string;
  status: string;
  numero: string;
}

// ─── Constantes ────────────────────────────────────────────────

const CATEGORIES: { value: WhatsAppTemplate["category"]; label: string; icon: string; color: string }[] = [
  { value: "MARKETING", label: "Marketing", icon: "ti-speakerphone", color: "primary" },
  { value: "UTILITY", label: "Utilidade", icon: "ti-tool", color: "success" },
  { value: "AUTHENTICATION", label: "Autenticação", icon: "ti-shield-lock", color: "warning" },
];

// Classificações de template (coluna templates.classificacao). "" = vazio/sem classificação.
const CLASSIFICACOES = ["Boas-vindas", "Manual", "Cadência"];
const FILTRO_VAZIOS = "__vazios__";

// Variáveis que os motores de automação (cadencia-worker / bemvindo-worker) resolvem
// pelo NOME do placeholder — NÃO são campos de card. Os nomes abaixo são exatamente os
// que os workers reconhecem (`_PH_CONTATO` / `_PH_AGENTE` em app/producao.py); mudar a
// grafia aqui faz o motor cair no default e mandar o dado errado.
const VARS_SISTEMA: { valor: string; rotulo: string }[] = [
  { valor: "nome_lead", rotulo: "Nome do contato (lead)" },
  { valor: "nome_user", rotulo: "Nome do atendente (usuário)" },
];
const VARS_SISTEMA_VALORES = VARS_SISTEMA.map((v) => v.valor);

// Optgroup fixo dos dois selects de variável (criação e "Editar variáveis").
const OPTGROUP_SISTEMA = (
  <optgroup label="Sistema (automação)">
    {VARS_SISTEMA.map((v) => (
      <option key={v.valor} value={v.valor}>{`${v.rotulo} — ${v.valor}`}</option>
    ))}
  </optgroup>
);

const LANGUAGES = [
  { value: "pt_BR", label: "Português (BR)" },
  { value: "en_US", label: "English (US)" },
  { value: "es", label: "Español" },
  { value: "es_AR", label: "Español (AR)" },
  { value: "es_MX", label: "Español (MX)" },
];

const HEADER_TYPES: { value: WhatsAppTemplate["header_type"]; label: string; icon: string }[] = [
  { value: "NONE", label: "Nenhum", icon: "ti-circle-off" },
  { value: "TEXT", label: "Texto", icon: "ti-text-recognition" },
  { value: "IMAGE", label: "Imagem", icon: "ti-photo" },
  { value: "VIDEO", label: "Vídeo", icon: "ti-video" },
];

// Limites de upload (Meta exige ≤16MB p/ template header)
const MAX_HEADER_BYTES = 16 * 1024 * 1024;

const STATUS_CONFIG: Record<WhatsAppTemplate["status"], { label: string; bg: string; text: string }> = {
  APPROVED: { label: "Aprovado", bg: "bg-soft-success", text: "text-success" },
  PENDING: { label: "Pendente", bg: "bg-soft-warning", text: "text-warning" },
  REJECTED: { label: "Rejeitado", bg: "bg-soft-danger", text: "text-danger" },
};

// ─── Helpers ───────────────────────────────────────────────────

const parseMetaTemplate = (mt: any): WhatsAppTemplate => {
  let headerType: WhatsAppTemplate["header_type"] = "NONE";
  let headerText = "";
  let body = "";
  let footer = "";
  const buttons: TemplateButton[] = [];

  for (const comp of mt.components || []) {
    if (comp.type === "HEADER" && comp.format === "TEXT") {
      headerType = "TEXT";
      headerText = comp.text || "";
    } else if (comp.type === "HEADER" && comp.format === "IMAGE") {
      headerType = "IMAGE";
    } else if (comp.type === "HEADER" && comp.format === "VIDEO") {
      headerType = "VIDEO";
    } else if (comp.type === "BODY") {
      body = comp.text || "";
    } else if (comp.type === "FOOTER") {
      footer = comp.text || "";
    } else if (comp.type === "BUTTONS") {
      for (const btn of comp.buttons || []) {
        buttons.push({
          type: btn.type === "QUICK_REPLY" ? "QUICK_REPLY" : btn.type === "URL" ? "URL" : "PHONE_NUMBER",
          text: btn.text || "",
          url: btn.url,
          phone_number: btn.phone_number,
        });
      }
    }
  }

  // Fallback de header_type via tipo_midia do banco (caso a Meta não traga o format)
  if (headerType === "NONE" && mt.tipo_midia === "imagem") headerType = "IMAGE";
  if (headerType === "NONE" && mt.tipo_midia === "video") headerType = "VIDEO";

  return {
    id: mt.id || String(Date.now()),
    name: mt.name,
    category: mt.category || "MARKETING",
    language: mt.language,
    status: mt.status || "PENDING",
    header_type: headerType,
    header_text: headerText || undefined,
    body,
    footer: footer || undefined,
    buttons,
    url_midia: mt.url_midia,
    tipo_midia: mt.tipo_midia,
    in_db: mt.in_db,
    texto_template: mt.texto_template,
    classificacao: mt.classificacao ?? null,
    _wabas: mt._wabas,
  };
};

// ─── Componente Principal ──────────────────────────────────────

const WhatsAppTemplates = () => {
  const routes = all_routes;
  const id_time = localStorage.getItem("id_cliente");
  const id_grupo = localStorage.getItem("id_grupo");
  const ins_nome = localStorage.getItem("instanciaoficial");

  // ─── Estado da Instância Oficial ─────────────────────────
  const [officialConfig, setOfficialConfig] = useState<OfficialConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");

  // ─── WABAs do grupo ────────────────────────────────────────
  // O grupo pode ter mais de uma WABA (a TNP tem 3). `selectedWaba` filtra a listagem;
  // TODAS mostra a união, com badge por template indicando onde ele existe.
  const [wabas, setWabas] = useState<WabaOption[]>([]);
  const [selectedWaba, setSelectedWaba] = useState<string>(TODAS);
  // WABAs marcadas no modal de criação (vazio = nenhuma; default = todas ao abrir).
  const [formWabas, setFormWabas] = useState<string[]>([]);
  // Resultado por WABA da última criação (sucesso e erro convivem).
  const [saveResults, setSaveResults] = useState<any[]>([]);

  // ─── Estado dos Templates ──────────────────────────────────
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Filtro por classificação ("" = todos; FILTRO_VAZIOS = sem classificação)
  const [filtroClassif, setFiltroClassif] = useState<string>("");
  const [buscaNome, setBuscaNome] = useState<string>("");
  // Modal de escolher a classificação (mostra o texto do template)
  const [showClassifModal, setShowClassifModal] = useState(false);
  const [classifTarget, setClassifTarget] = useState<WhatsAppTemplate | null>(null);
  const [classifValue, setClassifValue] = useState<string>("");
  const [savingClassif, setSavingClassif] = useState(false);

  // ─── Modal de Criação ────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  // ─── Form States ───────────────────────────────────────────
  const [formName, setFormName] = useState("");
  // UTILITY por padrão: a Meta revisa mais rápido e reclassifica p/ MARKETING sozinha quando
  // é o caso. Categoria de template APROVADO não é mais editável — errar aqui não tem conserto.
  const [formCategory, setFormCategory] = useState<WhatsAppTemplate["category"]>("UTILITY");
  const [formLanguage, setFormLanguage] = useState("pt_BR");
  const [formHeaderType, setFormHeaderType] = useState<WhatsAppTemplate["header_type"]>("NONE");
  const [formHeaderText, setFormHeaderText] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formFooter, setFormFooter] = useState("");
  const [formButtons, setFormButtons] = useState<TemplateButton[]>([]);
  const [formBodyExamples, setFormBodyExamples] = useState<string[]>([]);
  // Nome de cada variável para salvar no banco (texto_template). Chave = número ("1","2"...).
  const [formVarNames, setFormVarNames] = useState<Record<string, string>>({});
  const [formVarModes, setFormVarModes] = useState<Record<string, "card" | "manual">>({});
  const [formHeaderExample, setFormHeaderExample] = useState("");
  const [formHeaderFile, setFormHeaderFile] = useState<File | null>(null);
  const [formHeaderPreviewUrl, setFormHeaderPreviewUrl] = useState<string>("");

  // IA "Ajustar para Utility"
  const [iaLoading, setIaLoading] = useState(false);
  const [iaErro, setIaErro] = useState("");
  const [iaResultado, setIaResultado] = useState<IaResultado | null>(null);
  const [iaSnapshot, setIaSnapshot] = useState<IaSnapshot | null>(null);

  // ─── Modal de Exclusão ─────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Vista da tela: os templates vivos (Meta) ou o historico de exclusoes (audit_log).
  const [vista, setVista] = useState<"ativos" | "excluidos">("ativos");
  const [excluidos, setExcluidos] = useState<any[]>([]);
  const [loadingExcluidos, setLoadingExcluidos] = useState(false);
  // Confirmacao de sucesso: antes da 15/09/2026 a exclusao so fazia o item sumir da
  // lista, sem dizer nada — o operador nao tinha como distinguir de um erro silencioso.
  const [deleteSuccess, setDeleteSuccess] = useState<string>("");
  const [templateToDelete, setTemplateToDelete] = useState<WhatsAppTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // ─── Cards (para nomear variáveis) ─────────────────────────
  const [cardFields, setCardFields] = useState<CardFieldOption[]>([]);

  // ─── Modal de Mapeamento de Variáveis (registrar/reeditar) ─
  const [showVarModal, setShowVarModal] = useState(false);
  const [varTarget, setVarTarget] = useState<WhatsAppTemplate | null>(null);
  // Por número de variável ("1","2"...): nome escolhido e modo (card | manual)
  const [varNames, setVarNames] = useState<Record<string, string>>({});
  const [varModes, setVarModes] = useState<Record<string, "card" | "manual">>({});
  const [savingVars, setSavingVars] = useState(false);
  const [varError, setVarError] = useState("");

  // ─── Verificação de Instância Oficial ─────────────────────

  useEffect(() => {
    if (!id_time) {
      setLoadingConfig(false);
      setConfigError("ID do time não encontrado no login.");
      return;
    }

    fetch(`${API_URL}/api/whatsapp/get_official_config`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time, ins_nome }),
    })
      .then((res) => {
        if (res.status === 404) throw new Error("NOT_CONFIGURED");
        if (!res.ok) throw new Error("FETCH_ERROR");
        return res.json();
      })
      .then((data) => {
        if (data.waba_id && data.official_token) {
          setOfficialConfig(data);
        } else {
          setConfigError("NOT_CONFIGURED");
        }
      })
      .catch((err) => {
        setConfigError(err.message === "NOT_CONFIGURED" ? "NOT_CONFIGURED" : "FETCH_ERROR");
      })
      .finally(() => setLoadingConfig(false));
  }, [id_time]);

  // ─── Carrega as WABAs do grupo ────────────────────────────

  useEffect(() => {
    if (!id_grupo && !id_time) return;
    fetch(`${API_URL}/api/whatsapp/wabas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_cliente: id_grupo, id_time }),
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: WabaOption[]) => setWabas(Array.isArray(data) ? data : []))
      .catch(() => setWabas([]));
  }, [id_grupo, id_time]);

  // ─── Carrega Templates da Meta ────────────────────────────

  const loadTemplates = useCallback(() => {
    if (!id_time || !officialConfig) return;
    setLoadingTemplates(true);

    fetch(`${API_URL}/api/whatsapp/templates`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_time, ins_nome, id_cliente: id_grupo, waba: selectedWaba }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Erro ao buscar templates");
        return res.json();
      })
      .then((data: any[]) => {
        const parsed = data.map(parseMetaTemplate);
        setTemplates(parsed);
        // Trocar de WABA troca a lista: se o template aberto não existe mais aqui, abre o 1º.
        setActiveTemplateId((atual) =>
          atual && parsed.some((t) => t.id === atual) ? atual : parsed[0]?.id ?? null
        );
      })
      .catch((err) => console.error("Erro ao carregar templates:", err))
      .finally(() => setLoadingTemplates(false));
  }, [id_time, officialConfig, selectedWaba, id_grupo, ins_nome]);

  // ─── Classificação dos templates ──────────────────────────
  // Busca por nome (case-insensitive, sem acento) + filtro de classificacao.
  const norm = (v: string) =>
    (v || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const passaFiltro = (t: WhatsAppTemplate) => {
    if (buscaNome.trim() && !norm(t.name).includes(norm(buscaNome))) return false;
    if (!filtroClassif) return true;
    if (filtroClassif === FILTRO_VAZIOS) return !t.classificacao;
    return t.classificacao === filtroClassif;
  };
  const filteredTemplates = templates.filter(passaFiltro);

  // Se o template ativo sair do filtro, seleciona o 1º visível.
  useEffect(() => {
    const ft = templates.filter(passaFiltro);
    if (ft.length && !ft.some((t) => t.id === activeTemplateId)) {
      setActiveTemplateId(ft[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroClassif, buscaNome, templates]);

  const openClassifModal = (t: WhatsAppTemplate) => {
    setClassifTarget(t);
    setClassifValue(t.classificacao || "");
    setShowClassifModal(true);
  };

  const saveClassif = async () => {
    if (!classifTarget) return;
    setSavingClassif(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/classificacao`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_time, name: classifTarget.name, classificacao: classifValue || null }),
      });
      if (!res.ok) throw new Error();
      setTemplates((prev) => prev.map((t) => (t.name === classifTarget.name ? { ...t, classificacao: classifValue || null } : t)));
      setShowClassifModal(false);
    } catch (e) {
      console.error(e);
      alert("Falha ao salvar a classificação.");
    } finally {
      setSavingClassif(false);
    }
  };

  useEffect(() => {
    if (officialConfig) loadTemplates();
  }, [officialConfig, loadTemplates]);

  // ─── Carrega campos de card (para nomear variáveis) ───────
  // Cards são keyed por id_time (= localStorage "id_cliente"). include_fields=true
  // traz os campos junto, evitando N requests por grupo.
  useEffect(() => {
    if (!id_time) return;
    fetch(`${API_URL}/card/groups?id_cliente=${id_time}&include_fields=true`)
      .then((res) => (res.ok ? res.json() : []))
      .then((groups: any[]) => {
        const opts: CardFieldOption[] = [];
        for (const g of groups || []) {
          for (const f of g.fields || []) {
            if (f?.label) opts.push({ group: g.nome || "Card", label: f.label });
          }
        }
        setCardFields(opts);
      })
      .catch((err) => console.error("Erro ao carregar campos de card:", err));
  }, [id_time]);

  // ─── Helpers ─────────────────────────────────────────────

  const getVariables = (text: string) => {
    const matches = text.match(/\{\{\d+\}\}/g);
    return matches ? Array.from(new Set(matches)).sort() : [];
  };

  const renderBodyPreview = (text: string) => {
    return text.replace(/\{\{(\d+)\}\}/g, '<span class="badge bg-soft-primary text-primary fs-10 mx-1">{{$1}}</span>');
  };

  // Números das variáveis do corpo ({{1}}, {{2}}...) únicos, em ordem.
  const getNumericVars = (text: string): string[] => {
    const nums: string[] = [];
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text || "")) !== null) {
      if (!nums.includes(m[1])) nums.push(m[1]);
    }
    return nums.sort((a, b) => Number(a) - Number(b));
  };

  // Nomes das variáveis nomeadas ({{nome}}) já salvas no banco, em ordem de aparição.
  const getNamedVars = (text: string): string[] => {
    const names: string[] = [];
    const re = /\{\{\s*([^}]+?)\s*\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text || "")) !== null) names.push(m[1].trim());
    return names;
  };

  // Variáveis para exibir no detalhe. Se o template está no banco, mostra o número
  // da Meta ao lado do nome salvo ({{1}} → {{nome_lead}}); senão, só o número ({{1}}).
  const getDisplayVars = (t: WhatsAppTemplate): string[] => {
    const nums = getNumericVars(t.body);
    if (t.in_db && t.texto_template) {
      const namedUniq = Array.from(new Set(getNamedVars(t.texto_template)));
      return nums.map((num, i) => {
        const name = namedUniq[i];
        return name && name !== num ? `{{${num}}} → {{${name}}}` : `{{${num}}}`;
      });
    }
    return nums.map((n) => `{{${n}}}`);
  };

  // ─── Modal de Variáveis: abrir / salvar ────────────────────

  const openVarModal = (template: WhatsAppTemplate) => {
    const numVars = getNumericVars(template.body);
    // Pré-preenche a partir do texto_template salvo (mapeamento posicional:
    // a 1ª variável numérica corresponde ao 1º nome salvo, e assim por diante).
    const savedNames = template.in_db ? getNamedVars(template.texto_template || "") : [];
    const names: Record<string, string> = {};
    const modes: Record<string, "card" | "manual"> = {};
    numVars.forEach((n, i) => {
      const saved = savedNames[i] && !/^\d+$/.test(savedNames[i]) ? savedNames[i] : "";
      names[n] = saved;
      // Se o nome salvo bate com um label de card OU com uma variável de sistema
      // (nome_lead/nome_user), abre no select; senão, no campo manual.
      const noSelect = saved && (cardFields.some((c) => c.label === saved) || VARS_SISTEMA_VALORES.includes(saved));
      modes[n] = noSelect ? "card" : saved ? "manual" : "card";
    });
    setVarNames(names);
    setVarModes(modes);
    setVarTarget(template);
    setVarError("");
    setShowVarModal(true);
  };

  const closeVarModal = () => {
    setShowVarModal(false);
    setVarTarget(null);
    setVarError("");
  };

  // Constrói o texto_template substituindo {{N}} pelo nome escolhido.
  // Variável sem nome definido permanece {{N}} (não quebra o template).
  const buildTextoTemplate = (body: string, names: Record<string, string>) =>
    body.replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, n) => {
      const nome = (names[n] || "").trim();
      return nome ? `{{${nome}}}` : `{{${n}}}`;
    });

  const handleSaveVars = async () => {
    if (!varTarget) return;
    setSavingVars(true);
    setVarError("");

    const texto_template = buildTextoTemplate(varTarget.body, varNames);

    // Em quais contas (WABA) este template existe de verdade — mesma fonte que o delete usa.
    // É isso que decide sob QUAL número a linha nasce no banco. Mandar só o `fone` do login,
    // como era até 21/09/2026, cadastrava o template da WABA B debaixo do número da WABA A:
    // a linha existia, o seletor do /chat oferecia e o envio falhava com 132001, além de dar
    // a impressão de que o template existia nas duas contas. Cada alvo leva o `id` da SUA
    // conta; `id` é PK no banco, então repetir o mesmo id entre WABAs estoura.
    const wabasDoTemplate =
      selectedWaba !== TODAS
        ? [{ waba_id: selectedWaba, id: varTarget.id }]
        : (varTarget._wabas || []).map((w) => ({ waba_id: w.waba_id, id: w.id || varTarget.id }));

    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_time,
          id_cliente: id_grupo,
          fone: officialConfig?.numero,   // fallback do back antigo; ignorado quando vai `wabas`
          wabas: wabasDoTemplate.length > 0 ? wabasDoTemplate : undefined,
          meta_id: varTarget.id,
          name: varTarget.name,
          language: varTarget.language,
          header_type: varTarget.header_type,
          texto_template,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setVarError(data.error || "Falha ao salvar no sistema.");
        return;
      }
      // Atualiza o estado local: marca como no banco e guarda o texto nomeado.
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === varTarget.id ? { ...t, in_db: true, texto_template } : t
        )
      );
      closeVarModal();
    } catch (err: any) {
      setVarError(`Erro de conexão: ${err.message}`);
    } finally {
      setSavingVars(false);
    }
  };

  // ─── Sync body examples com variáveis ────────────────────

  useEffect(() => {
    const vars = getVariables(formBody);
    setFormBodyExamples((prev) => {
      const newExamples = [...prev];
      while (newExamples.length < vars.length) newExamples.push("");
      return newExamples.slice(0, vars.length);
    });
  }, [formBody]);

  useEffect(() => {
    const hasHeaderVar = /\{\{\d+\}\}/.test(formHeaderText);
    if (!hasHeaderVar) setFormHeaderExample("");
  }, [formHeaderText]);

  // ─── Handlers ──────────────────────────────────────────────

  const openCreateModal = () => {
    setFormName("");
    setFormCategory("UTILITY");
    setFormLanguage("pt_BR");
    /*
     * Escolha da WABA é OBRIGATÓRIA quando o grupo tem mais de uma conta.
     *
     * Antes o modal abria com TODAS marcadas quando o seletor estava em "Todas as WABAs",
     * então dava para criar o template em todas as contas sem nunca ter escolhido — o
     * default decidia por quem operava. Com várias WABAs o modal agora abre VAZIO e o
     * salvar fica bloqueado até uma ser marcada.
     *
     * Com uma WABA só não há o que escolher: marca ela e segue.
     * Se o seletor já estiver apontando para UMA WABA específica, essa escolha vale como
     * a decisão do operador e vem marcada.
     */
    if (wabas.length <= 1) {
      setFormWabas(wabas.map((w) => w.waba_id));
    } else {
      setFormWabas(selectedWaba === TODAS ? [] : [selectedWaba]);
    }
    setSaveResults([]);
    setFormHeaderType("NONE");
    setFormHeaderText("");
    setFormBody("");
    setFormFooter("");
    setFormButtons([]);
    setFormBodyExamples([]);
    setFormVarNames({});
    setFormVarModes({});
    setFormHeaderExample("");
    setFormHeaderFile(null);
    if (formHeaderPreviewUrl) URL.revokeObjectURL(formHeaderPreviewUrl);
    setFormHeaderPreviewUrl("");
    setIaLoading(false);
    setIaErro("");
    setIaResultado(null);
    setIaSnapshot(null);
    setSaveError("");
    setSaveSuccess("");
    setShowModal(true);
  };

  const handleHeaderFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (formHeaderPreviewUrl) URL.revokeObjectURL(formHeaderPreviewUrl);
    if (!file) {
      setFormHeaderFile(null);
      setFormHeaderPreviewUrl("");
      return;
    }
    if (file.size > MAX_HEADER_BYTES) {
      setSaveError(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Limite: 16MB.`);
      e.target.value = "";
      return;
    }
    const isImage = formHeaderType === "IMAGE";
    const isVideo = formHeaderType === "VIDEO";
    if (isImage && !file.type.startsWith("image/")) {
      setSaveError("Selecione um arquivo de imagem.");
      e.target.value = "";
      return;
    }
    if (isVideo && !file.type.startsWith("video/")) {
      setSaveError("Selecione um arquivo de vídeo.");
      e.target.value = "";
      return;
    }
    setSaveError("");
    setFormHeaderFile(file);
    setFormHeaderPreviewUrl(URL.createObjectURL(file));
  };

  // Limpa file/preview ao trocar o header_type para algo que não usa mídia
  useEffect(() => {
    if (formHeaderType !== "IMAGE" && formHeaderType !== "VIDEO") {
      if (formHeaderPreviewUrl) URL.revokeObjectURL(formHeaderPreviewUrl);
      setFormHeaderFile(null);
      setFormHeaderPreviewUrl("");
    }
  }, [formHeaderType]);

  const closeModal = () => {
    setShowModal(false);
    setSaveError("");
    setSaveSuccess("");
  };

  const handleCreateTemplate = async () => {
    if (!formName.trim() || !formBody.trim()) return;
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");

    const bodyVars = getVariables(formBody);
    const hasHeaderVar = /\{\{\d+\}\}/.test(formHeaderText);
    const isMediaHeader = formHeaderType === "IMAGE" || formHeaderType === "VIDEO";

    // Valida exemplos obrigatórios
    if (bodyVars.length > 0 && formBodyExamples.some((ex) => !ex.trim())) {
      setSaveError("Preencha todos os exemplos das variáveis do corpo.");
      setSaving(false);
      return;
    }
    if (hasHeaderVar && !formHeaderExample.trim()) {
      setSaveError("Preencha o exemplo da variável do header.");
      setSaving(false);
      return;
    }
    if (isMediaHeader && !formHeaderFile) {
      setSaveError(`Selecione um arquivo de ${formHeaderType === "IMAGE" ? "imagem" : "vídeo"} para o header.`);
      setSaving(false);
      return;
    }
    if (wabas.length > 0 && formWabas.length === 0) {
      setSaveError(
        wabas.length > 1
          ? "Escolha em qual conta (WABA) o template será criado. Como este login tem "
            + `${wabas.length} contas, a escolha é obrigatória.`
          : "Selecione ao menos uma WABA para criar o template."
      );
      setSaving(false);
      return;
    }

    const templatePayload: any = {
      name: formName,
      category: formCategory,
      language: formLanguage,
      header_type: formHeaderType,
      header_text: formHeaderType === "TEXT" ? formHeaderText : undefined,
      body: formBody,
      footer: formFooter || undefined,
      buttons: formButtons,
      body_examples: formBodyExamples.length > 0 ? formBodyExamples : undefined,
      header_example: hasHeaderVar ? formHeaderExample : undefined,
      // Versão nomeada salva no banco: a Meta recebe {{N}}, o banco recebe {{nome}}.
      // Variável sem nome definido fica como {{N}} (igual ao body).
      texto_template: buildTextoTemplate(formBody, formVarNames),
    };

    // `wabas` é o que decide onde criar. Só é omitido quando o grupo não tem WABA listada
    // (base antiga) — aí o backend cai no comportamento de sempre: a instância do login.
    const wabasPayload = wabas.length > 0 ? formWabas : undefined;

    try {
      let res: Response;
      if (isMediaHeader && formHeaderFile) {
        const fd = new FormData();
        fd.append("template", JSON.stringify(templatePayload));
        fd.append("id_time", String(id_time ?? ""));
        if (id_grupo) fd.append("id_cliente", String(id_grupo));
        if (ins_nome) fd.append("ins_nome", String(ins_nome));
        if (officialConfig?.numero) fd.append("fone", String(officialConfig.numero));
        if (wabasPayload) fd.append("wabas", JSON.stringify(wabasPayload));
        fd.append("file", formHeaderFile);
        res = await fetch(`${API_URL}/api/whatsapp/templates/create`, {
          method: "POST",
          body: fd, // sem Content-Type explícito (boundary auto)
        });
      } else {
        res = await fetch(`${API_URL}/api/whatsapp/templates/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_time, id_cliente: id_grupo, ins_nome, fone: officialConfig?.numero, wabas: wabasPayload, template: templatePayload }),
        });
      }
      const data = await res.json();
      const results: any[] = Array.isArray(data.results) ? data.results : [];
      setSaveResults(results);

      if (res.ok) {
        const falhas = results.filter((r) => !r.ok);
        if (falhas.length > 0) {
          // Criação parcial: não fecha o modal — o operador precisa ver onde não entrou.
          setSaveSuccess(`Criado em ${results.length - falhas.length} de ${results.length} WABA(s).`);
          setSaveError("");
          loadTemplates();
        } else {
          setSaveSuccess(
            results.length > 1
              ? `Template enviado para aprovação da Meta nas ${results.length} WABAs!`
              : "Template enviado para aprovação da Meta!"
          );
          setTimeout(() => {
            closeModal();
            loadTemplates();
          }, 1500);
        }
      } else {
        const details = data.details?.error?.message || data.details?.error?.error_user_msg || data.error || JSON.stringify(data.details);
        setSaveError(`Erro da Meta: ${details}`);
      }
    } catch (err: any) {
      setSaveError(`Erro de conexão: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Historico de exclusoes: vem do audit_log via /api/whatsapp/templates/excluidos.
  // Nao existe tabela de lixeira — a trilha de auditoria E a fonte.
  const loadExcluidos = async () => {
    setLoadingExcluidos(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/excluidos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente: id_grupo, id_time }),
      });
      const data = await res.json().catch(() => ({} as any));
      setExcluidos(res.ok ? (data.excluidos || []) : []);
    } catch (e) {
      console.error("excluidos:", e);
      setExcluidos([]);
    } finally {
      setLoadingExcluidos(false);
    }
  };

  useEffect(() => {
    if (vista === "excluidos") loadExcluidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista]);

  const confirmDelete = (template: WhatsAppTemplate) => {
    setTemplateToDelete(template);
    setDeleteError("");
    setDeleteSuccess("");   // nao deixar a confirmacao anterior na tela
    setShowDeleteModal(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    setDeleteError("");

    // 🔴 Onde apagar: com o seletor numa WABA, é ela; em "Todas", as WABAs onde o
    // template REALMENTE existe (`_wabas`, que a listagem já traz). Sem isso a rota caía
    // na WABA do login e a Meta recusava quando o template era de outra conta — foi o que
    // aconteceu com os `isir_*` do time 5141 (WABA das instâncias 53/54).
    const alvos =
      selectedWaba !== TODAS
        ? [selectedWaba]
        : (templateToDelete._wabas || []).map((w) => w.waba_id);

    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_time,
          ins_nome,
          id_cliente: id_grupo,
          // id_user: e o que faz a auditoria saber QUEM excluiu. Sem isto o audit_log grava
          // id_user=None e so sobra o nome do template + IP — foi o caso ate 15/09/2026.
          id_user: localStorage.getItem("id_user") || undefined,
          name: templateToDelete.name,
          wabas: alvos.length > 0 ? alvos : undefined,
        }),
      });

      const data = await res.json().catch(() => ({} as any));

      // Antes isto era um `if (res.ok)` sem `else`, e o modal fechava no `finally`: no 400
      // a tela não dizia nada e o template continuava na lista — o "não acontece nada" que
      // foi reportado.
      if (!res.ok) {
        setDeleteError(data?.error || "Não foi possível excluir o template. Tente novamente.");
        return;
      }

      // Confirmacao explicita. `linhas_removidas` = 0 com `apagados` > 0 significa que
      // o template saiu da Meta mas FICOU na tabela local — o orfao que continua
      // aparecendo no seletor do /chat. O operador precisa saber disso na hora.
      const wabasOk = Array.isArray(data?.results) ? data.results.filter((r: any) => r.ok).length : 0;
      const wabasTot = Array.isArray(data?.results) ? data.results.length : 0;
      const parcial = wabasTot > 1 && wabasOk < wabasTot;
      setDeleteSuccess(
        parcial
          ? `"${templateToDelete.name}" excluido em ${wabasOk} de ${wabasTot} contas. Verifique as demais.`
          : `"${templateToDelete.name}" excluido com sucesso.`
      );
      setTemplates((prev) => prev.filter((t) => t.name !== templateToDelete.name));
      if (activeTemplateId === templateToDelete.id) {
        const remaining = templates.filter((t) => t.id !== templateToDelete.id);
        setActiveTemplateId(remaining.length > 0 ? remaining[0].id : null);
      }
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    } catch (err: any) {
      console.error("Erro ao deletar template:", err);
      setDeleteError(`Erro de conexão: ${err?.message || "servidor não respondeu"}`);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Button Handlers ──────────────────────────────────────
  const addButton = () => {
    if (formButtons.length >= 3) return;
    setFormButtons((prev) => [...prev, { type: "QUICK_REPLY", text: "" }]);
  };

  const updateButton = (index: number, updates: Partial<TemplateButton>) => {
    setFormButtons((prev) => prev.map((btn, i) => (i === index ? { ...btn, ...updates } : btn)));
  };

  const removeButton = (index: number) => {
    setFormButtons((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── IA: "Ajustar para Utility" ───────────────────────────
  // Reescreve header/corpo/rodapé/labels dos botões pela regra de UTILITY da Meta
  // (prompt do 08-prompt-gpt-converter-template-utility.md, no backend). Nada é salvo:
  // o form recebe a sugestão e o snapshot anterior fica guardado para "Desfazer".

  const ajustarParaUtility = async () => {
    if (!formBody.trim() || iaLoading) return;
    setIaLoading(true);
    setIaErro("");
    setIaResultado(null);
    const snapshot: IaSnapshot = {
      category: formCategory,
      headerText: formHeaderText,
      body: formBody,
      footer: formFooter,
      buttons: formButtons,
      bodyExamples: formBodyExamples,
    };
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/templates/ajustar_utility`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template: {
            category: formCategory,
            language: formLanguage,
            header_type: formHeaderType,
            header_text: formHeaderText,
            body: formBody,
            footer: formFooter,
            buttons: formButtons,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setIaErro(data.error || "A IA não conseguiu ajustar o template.");
        return;
      }
      setIaResultado(data);
      if (!data.convertivel) return;

      setIaSnapshot(snapshot);
      setFormCategory("UTILITY");
      if (formHeaderType === "TEXT" && data.header) setFormHeaderText(data.header);
      setFormBody(data.body);
      // Rodapé que a IA zerou era promocional ("Siga nossas redes"): sai junto.
      setFormFooter(data.footer || "");
      if (Array.isArray(data.botoes) && data.botoes.length === formButtons.length) {
        setFormButtons((prev) => prev.map((b, i) => ({ ...b, text: data.botoes[i] || b.text })));
      }
      // Exemplo que o operador já tinha digitado vale mais que o da IA.
      if (Array.isArray(data.exemplos_body)) {
        setFormBodyExamples(data.exemplos_body.map((ex: string, i: number) => formBodyExamples[i]?.trim() || ex || ""));
      }
    } catch (err: any) {
      setIaErro(`Erro de conexão: ${err.message}`);
    } finally {
      setIaLoading(false);
    }
  };

  const desfazerAjusteIa = () => {
    if (!iaSnapshot) return;
    setFormCategory(iaSnapshot.category);
    setFormHeaderText(iaSnapshot.headerText);
    setFormBody(iaSnapshot.body);
    setFormFooter(iaSnapshot.footer);
    setFormButtons(iaSnapshot.buttons);
    setFormBodyExamples(iaSnapshot.bodyExamples);
    setIaSnapshot(null);
    setIaResultado(null);
  };

  // ─── Render: Loading ─────────────────────────────────────

  if (loadingConfig) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Verificando instância oficial...</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Sem Instância Oficial ───────────────────────

  if (configError || !officialConfig) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-4">
            <div className="d-flex align-items-center gap-2 mb-1">
              <i className="ti ti-brand-whatsapp fs-24 text-success"></i>
              <h4 className="page-title mb-0 text-dark fw-bold">Templates WhatsApp</h4>
            </div>
          </div>
          <div className="text-center py-5 rounded-4 border mx-0" style={{ background: "#fafafa" }}>
            <i className="ti ti-shield-off fs-48 text-muted mb-3 d-block"></i>
            <h5 className="fw-bold text-dark mb-2">Instância Oficial não configurada</h5>
            <p className="text-muted small mb-3" style={{ maxWidth: 480, margin: "0 auto" }}>
              Para criar e gerenciar templates, é necessário ter uma instância oficial da Meta (WhatsApp Business API)
              configurada com WABA ID e Token de acesso.
            </p>
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="d-flex align-items-center gap-2 text-muted fs-13">
                <i className="ti ti-circle-check text-success"></i>
                <span>Configure em <strong>Configurações &gt; WhatsApp Oficial</strong></span>
              </div>
              <div className="d-flex align-items-center gap-2 text-muted fs-13">
                <i className="ti ti-circle-check text-success"></i>
                <span>Necessário: WABA ID, Phone ID e Token permanente</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Principal ───────────────────────────────────

  const activeTemplate = templates.find((t) => t.id === activeTemplateId);
  const bodyVarsInForm = getVariables(formBody);
  const headerHasVar = /\{\{\d+\}\}/.test(formHeaderText);

  // Rótulo da WABA = os números dela. O waba_id não diz nada a quem opera; o número, sim.
  const wabaLabel = (wabaId: string) => {
    const w = wabas.find((x) => x.waba_id === wabaId);
    if (!w || w.instancias.length === 0) return wabaId;
    return w.instancias.map((i) => i.ins_numero).join(" · ");
  };
  const multiWaba = wabas.length > 1;
  /*
   * Com mais de uma WABA no login, escolher o destino é obrigatório: enquanto nenhuma
   * estiver marcada o salvar fica travado e o campo aparece em vermelho. Com uma WABA só
   * não há escolha a fazer, e o modal já a marca sozinho.
   */
  const precisaEscolherWaba = multiWaba && formWabas.length === 0;

  // Campos de card agrupados (para o select do modal de variáveis)
  const groupedCardFields = cardFields.reduce<Record<string, string[]>>((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c.label);
    return acc;
  }, {});
  const varTargetNums = varTarget ? getNumericVars(varTarget.body) : [];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <div className="d-flex align-items-center gap-2 mb-1">
                <i className="ti ti-brand-whatsapp fs-24 text-success"></i>
                <h4 className="page-title mb-0 text-dark fw-bold">Templates WhatsApp</h4>
              </div>
              <p className="text-muted fs-13 mb-0">
                <i className="ti ti-shield-check me-1 text-success"></i>
                Instância oficial conectada — {officialConfig.numero || officialConfig.waba_id}
              </p>
            </div>
            <div className="col-auto d-flex align-items-center gap-3">
              {/* Ativos x Excluidos. Os excluidos nao vem da Meta (la some de vez):
                  saem do audit_log, que e o unico registro de que existiram. */}
              <div className="btn-group btn-group-sm" role="group">
                <button type="button"
                  className={`btn ${vista === "ativos" ? "btn-primary" : "btn-outline-secondary"} rounded-pill px-3`}
                  onClick={() => setVista("ativos")}>
                  <i className="ti ti-list me-1" /> Ativos
                </button>
                <button type="button"
                  className={`btn ${vista === "excluidos" ? "btn-primary" : "btn-outline-secondary"} rounded-pill px-3 ms-1`}
                  onClick={() => setVista("excluidos")}>
                  <i className="ti ti-trash me-1" /> Excluidos
                </button>
              </div>
              {/* Seletor de WABA — só aparece se o grupo tiver mais de uma */}
              {multiWaba && (
                <div className="d-flex align-items-center gap-2">
                  <label className="fs-12 fw-bold text-muted mb-0 text-nowrap">WABA</label>
                  <Form.Select
                    size="sm"
                    className="border-2 shadow-none rounded-pill"
                    style={{ minWidth: 220 }}
                    value={selectedWaba}
                    onChange={(e) => setSelectedWaba(e.target.value)}
                    title="Filtra a lista pela conta do WhatsApp (WABA)"
                  >
                    <option value={TODAS}>Todas as WABAs ({wabas.length})</option>
                    {wabas.map((w) => (
                      <option key={w.waba_id} value={w.waba_id}>
                        {wabaLabel(w.waba_id)}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}

              {/* Contadores de Status */}
              <div className="d-flex gap-2">
                {(["APPROVED", "PENDING", "REJECTED"] as const).map((status) => {
                  const count = templates.filter((t) => t.status === status).length;
                  if (count === 0) return null;
                  const cfg = STATUS_CONFIG[status];
                  return (
                    <span key={status} className={`badge ${cfg.bg} ${cfg.text} rounded-pill px-2 py-1 fs-11`}>
                      {count} {cfg.label}
                    </span>
                  );
                })}
              </div>
              <button
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 rounded-pill px-3"
                onClick={loadTemplates}
                disabled={loadingTemplates}
              >
                <i className={`ti ti-refresh fs-14 ${loadingTemplates ? "spin" : ""}`} />
                <span className="d-none d-md-inline">Atualizar</span>
              </button>
              <button
                className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm"
                onClick={openCreateModal}
              >
                <i className="ti ti-plus fs-16" /> <span>Novo Template</span>
              </button>
            </div>
          </div>
        </div>

        {/* Confirmacao de exclusao — fica ate o operador fechar ou trocar de vista */}
        {deleteSuccess && (
          <div className="alert alert-success d-flex align-items-center justify-content-between fs-13 py-2 px-3 mb-3">
            <span><i className="ti ti-circle-check me-2" />{deleteSuccess}</span>
            <button type="button" className="btn-close btn-sm" onClick={() => setDeleteSuccess("")} />
          </div>
        )}

        {/* ─── Vista: EXCLUIDOS ─────────────────────────────── */}
        {vista === "excluidos" && (
          <div className="border rounded-4 overflow-hidden shadow-sm">
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
              <div>
                <span className="fs-11 fw-bold text-uppercase text-muted letter-spacing-1">Templates excluidos</span>
                <p className="text-muted fs-12 mb-0">
                  Historico da trilha de auditoria. O nome fica bloqueado na Meta por 30 dias apos a exclusao.
                </p>
              </div>
              <button className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                onClick={loadExcluidos} disabled={loadingExcluidos}>
                {loadingExcluidos ? <Spinner animation="border" size="sm" /> : <><i className="ti ti-refresh me-1" />Atualizar</>}
              </button>
            </div>
            {loadingExcluidos && excluidos.length === 0 ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" size="sm" /></div>
            ) : excluidos.length === 0 ? (
              <div className="text-center py-5 text-muted fs-13">
                <i className="ti ti-trash-off fs-40 d-block mb-2 opacity-50" />
                Nenhum template excluido registrado.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0 align-middle fs-13">
                  <thead className="table-light">
                    <tr>
                      <th>Template</th><th>Excluido em</th><th>Por</th><th className="text-center">Contas</th><th>Situacao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excluidos.map((e, i) => (
                      <tr key={`${e.template}-${e.excluido_em}-${i}`}>
                        <td className="fw-medium">{e.template}</td>
                        <td className="text-muted">
                          {e.excluido_em ? new Date(e.excluido_em).toLocaleString("pt-BR") : "—"}
                        </td>
                        <td>
                          {e.operador?.nome || e.operador?.login ? (
                            <>
                              <div>{e.operador?.nome || "—"}</div>
                              {e.operador?.login && <div className="text-muted fs-11">{e.operador.login}</div>}
                            </>
                          ) : (
                            /* Exclusoes anteriores a 15/09/2026 nao gravavam o operador. */
                            <span className="text-muted fst-italic fs-12">nao registrado</span>
                          )}
                        </td>
                        <td className="text-center text-muted">{e.wabas_apagadas ?? "—"}</td>
                        <td>
                          {/* "Excluido" pode ser meia verdade: a rota devolve 200 se ao menos
                              UMA WABA aceitou. Se o nome ainda existe em outra conta, o mesmo
                              template aparece aqui E na lista de ativos — precisa dizer por que. */}
                          {e.ainda_existe === true && (
                            <span className="badge bg-soft-danger text-danger me-1"
                              title={`Exclusao PARCIAL: continua ativo em ${(e.wabas_onde_existe || []).length} outra(s) conta(s) WABA. Por isso ele tambem aparece na lista de ativos.`}>
                              <i className="ti ti-alert-circle me-1" />ainda ativo
                            </span>
                          )}
                          {e.ainda_existe === null && (
                            <span className="badge bg-soft-secondary text-secondary me-1"
                              title="Uma das WABAs do grupo nao respondeu — nao da para confirmar se o template ainda existe.">
                              <i className="ti ti-help-circle me-1" />nao confirmado
                            </span>
                          )}
                          {e.orfao_potencial && (
                            <span className="badge bg-soft-warning text-warning" title="Saiu da Meta mas a linha local nao foi removida">
                              <i className="ti ti-alert-triangle me-1" />orfao
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Loading Templates */}
        {vista === "ativos" && loadingTemplates && templates.length === 0 && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="text-muted mt-2 fs-13">Carregando templates da Meta...</p>
          </div>
        )}

        {/* Conteúdo Principal */}
        {vista === "ativos" && (!loadingTemplates && templates.length === 0 ? (
          <div className="text-center py-5 rounded-4 border-dashed mx-0 card-builder-empty">
            <i className="ti ti-brand-whatsapp fs-40 text-muted mb-3"></i>
            <h5>Nenhum template criado ainda</h5>
            <p className="text-muted small">
              Templates são modelos de mensagem pré-aprovados pela Meta para envio via WhatsApp Business API.
            </p>
            <Button variant="primary" className="rounded-pill px-4" onClick={openCreateModal}>
              Criar Primeiro Template
            </Button>
          </div>
        ) : templates.length > 0 && (
          <div className="card-builder-vertical-container">
            <Tab.Container
              id="whatsapp-templates-tabs"
              activeKey={activeTemplateId || templates[0]?.id}
              onSelect={(key) => setActiveTemplateId(key)}
            >
              <div className="row g-0 border rounded-4 overflow-hidden shadow-sm card-builder-container" style={{ minHeight: window.innerWidth < 768 ? "auto" : "600px" }}>
                {/* Sidebar de Templates */}
                <div className="col-12 col-md-4 border-end card-builder-sidebar d-flex flex-column" style={{ maxHeight: window.innerWidth < 768 ? "none" : 600 }}>
                  <div className="p-3 border-bottom card-builder-sidebar-header flex-shrink-0">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fs-11 fw-bold text-uppercase text-muted letter-spacing-1">Templates</span>
                      <span className="badge bg-soft-secondary text-secondary rounded-pill">{filteredTemplates.length}</span>
                    </div>
                    {/* Busca por nome */}
                    <div className="position-relative mb-2">
                      <i className="ti ti-search position-absolute text-muted"
                         style={{ left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14 }} />
                      <Form.Control size="sm" type="text" className="border shadow-none fs-12"
                        style={{ paddingLeft: 30, paddingRight: buscaNome ? 28 : undefined }}
                        placeholder="Pesquisar por nome..."
                        value={buscaNome} onChange={(e) => setBuscaNome(e.target.value)} />
                      {buscaNome && (
                        <button type="button" className="btn btn-sm p-0 position-absolute text-muted"
                          style={{ right: 8, top: "50%", transform: "translateY(-50%)", lineHeight: 1 }}
                          onClick={() => setBuscaNome("")} title="Limpar">
                          <i className="ti ti-x" style={{ fontSize: 14 }} />
                        </button>
                      )}
                    </div>
                    {/* Filtro por classificação */}
                    <Form.Select size="sm" className="border shadow-none fs-12" value={filtroClassif} onChange={(e) => setFiltroClassif(e.target.value)}>
                      <option value="">Todas as classificações</option>
                      {CLASSIFICACOES.map((c) => (<option key={c} value={c}>{c}</option>))}
                      <option value={FILTRO_VAZIOS}>Sem classificação</option>
                    </Form.Select>
                  </div>
                  <Nav variant="pills" className="flex-column p-2 gap-1 custom-nav-pills builder-sidebar-nav" style={{ display: "flex", flexDirection: "column", flexWrap: "nowrap", overflowY: "auto", flex: 1, minHeight: 0 }}>
                    {filteredTemplates.length === 0 ? (
                      <div className="text-center text-muted fs-12 py-4">{buscaNome ? `Nenhum template com "${buscaNome}".` : "Nenhum template nesta classificação."}</div>
                    ) : filteredTemplates.map((template) => {
                      const catConfig = CATEGORIES.find((c) => c.value === template.category);
                      const statusCfg = STATUS_CONFIG[template.status];
                      // Template ativo (mesma lógica do activeKey das Tabs): o pill fica
                      // colorido, então o ícone de tag cinza some — deixa branco no ativo.
                      const isActive = template.id === (activeTemplateId || templates[0]?.id);
                      return (
                        <Nav.Item key={template.id}>
                          <div className="position-relative">
                            <Nav.Link
                              eventKey={template.id}
                              className="d-flex align-items-center border-0 rounded-3 py-2 ps-3 pe-5 fs-13 transition-all"
                            >
                              <div className="d-flex align-items-start gap-2 w-100">
                                <i className={`ti ${catConfig?.icon || "ti-template"} text-${catConfig?.color || "muted"} fs-16`} style={{ marginTop: 2 }}></i>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <span className="fw-medium d-block" style={{ wordBreak: "break-word", lineHeight: 1.3 }}>
                                    {template.name}
                                  </span>
                                  <span className={`fs-10 ${statusCfg?.text}`}>{statusCfg?.label}</span>
                                  {template.classificacao && (
                                    <span className="badge bg-soft-info text-info fs-10 rounded-pill px-2 ms-1">{template.classificacao}</span>
                                  )}
                                  {template.in_db === false && (
                                    <span className="badge bg-soft-warning text-warning fs-10 rounded-pill px-2 ms-1" title="Não registrado no sistema (tabela de templates)">
                                      <i className="ti ti-database-off fs-10 me-1"></i>fora do sistema
                                    </span>
                                  )}
                                  {/* Cobertura entre WABAs: só faz sentido na visão "Todas".
                                      Faltando em alguma, o badge diz em quais — é o sinal de
                                      que o envio por aquele número vai falhar. */}
                                  {multiWaba && selectedWaba === TODAS && template._wabas && (
                                    (() => {
                                      const presentes = template._wabas!.map((w) => w.waba_id);
                                      const faltando = wabas.filter((w) => !presentes.includes(w.waba_id));
                                      return faltando.length === 0 ? (
                                        <span className="badge bg-soft-success text-success fs-10 rounded-pill px-2 ms-1" title="Existe em todas as WABAs do grupo">
                                          <i className="ti ti-check fs-10 me-1"></i>{presentes.length} WABAs
                                        </span>
                                      ) : (
                                        <span
                                          className="badge bg-soft-danger text-danger fs-10 rounded-pill px-2 ms-1"
                                          title={`Falta em: ${faltando.map((w) => wabaLabel(w.waba_id)).join(" | ")}`}
                                        >
                                          <i className="ti ti-alert-triangle fs-10 me-1"></i>
                                          {presentes.length}/{wabas.length} WABAs
                                        </span>
                                      );
                                    })()
                                  )}
                                </div>
                              </div>
                            </Nav.Link>
                            <button
                              type="button"
                              className={`btn btn-sm p-1 ${isActive ? "text-white" : "text-muted"} position-absolute`}
                              style={{ top: 8, right: 4, zIndex: 2, background: "transparent", border: "none" }}
                              title="Ver texto e classificar"
                              onClick={() => openClassifModal(template)}
                            >
                              <i className="ti ti-tag fs-15" />
                            </button>
                          </div>
                        </Nav.Item>
                      );
                    })}
                  </Nav>
                </div>

                {/* Área de Detalhes + Preview */}
                <div className="col-12 col-md-8 card-builder-content" style={{ maxHeight: window.innerWidth < 768 ? "none" : 600, overflowY: "auto" }}>
                  <Tab.Content className="h-100">
                    {filteredTemplates.map((template) => {
                      const catConfig = CATEGORIES.find((c) => c.value === template.category);
                      const statusCfg = STATUS_CONFIG[template.status];
                      const langLabel = LANGUAGES.find((l) => l.value === template.language)?.label || template.language;
                      const variables = getDisplayVars(template);

                      return (
                        <Tab.Pane key={template.id} eventKey={template.id} className="h-100 animate__animated animate__fadeIn animate__faster">
                          <div className="p-4 d-flex flex-column h-100">
                            {/* Header do Template */}
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <h5 className="mb-0 fw-bold text-dark">{template.name}</h5>
                                  <span className={`badge ${statusCfg.bg} ${statusCfg.text} fs-10 rounded-pill px-2`}>
                                    {statusCfg.label.toUpperCase()}
                                  </span>
                                  <span className={`badge bg-soft-${catConfig?.color} text-${catConfig?.color} fs-10 rounded-pill px-2`}>
                                    {catConfig?.label}
                                  </span>
                                </div>
                                <p className="text-muted small mb-0">
                                  <i className="ti ti-language me-1"></i>{langLabel}
                                  {variables.length > 0 && (
                                    <span className="ms-3">
                                      <i className="ti ti-variable me-1"></i>{variables.length} variáve{variables.length > 1 ? "is" : "l"}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="d-flex gap-2">
                                {template.in_db === false ? (
                                  <button
                                    className="btn btn-warning btn-sm d-flex align-items-center gap-1 rounded-pill px-3 fw-medium"
                                    onClick={() => openVarModal(template)}
                                  >
                                    <i className="ti ti-database-import fs-14" /> Registrar no sistema
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 rounded-pill px-3"
                                    onClick={() => openVarModal(template)}
                                  >
                                    <i className="ti ti-variable fs-14" /> Editar variáveis
                                  </button>
                                )}
                                <button
                                  className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 rounded-pill px-3"
                                  onClick={() => confirmDelete(template)}
                                >
                                  <i className="ti ti-trash fs-14" /> Excluir
                                </button>
                              </div>
                            </div>

                            {/* Faixa: template não registrado no banco */}
                            {template.in_db === false && (
                              <Alert variant="warning" className="d-flex align-items-center gap-2 py-2 fs-12 mb-3">
                                <i className="ti ti-alert-triangle fs-16"></i>
                                <span className="flex-grow-1">
                                  Este template existe na Meta mas <strong>não está registrado no sistema</strong>.
                                  Registre-o para nomear as variáveis (cards/automação) e usá-lo nas cadências.
                                </span>
                                <button
                                  className="btn btn-warning btn-sm rounded-pill px-3 fw-medium flex-shrink-0"
                                  onClick={() => openVarModal(template)}
                                >
                                  <i className="ti ti-database-import fs-13 me-1"></i>Registrar
                                </button>
                              </Alert>
                            )}

                            {/* Conteúdo: Detalhes + Preview lado a lado */}
                            <div className="row flex-grow-1">
                              {/* Detalhes do Template */}
                              <div className="col-12 col-md-7">
                                <div className="d-flex flex-column gap-3">
                                  {/* Header */}
                                  {template.header_type !== "NONE" && (
                                    <div className="bg-light p-3 rounded-3 border">
                                      <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">Header</span>
                                      <div className="d-flex align-items-center gap-3">
                                        <i className={`ti ${HEADER_TYPES.find((h) => h.value === template.header_type)?.icon} text-muted fs-16`}></i>
                                        {template.header_type === "TEXT" && (
                                          <span className="fs-13">{template.header_text}</span>
                                        )}
                                        {template.header_type === "IMAGE" && template.url_midia && (
                                          <img
                                            src={template.url_midia}
                                            alt="header"
                                            className="rounded-2 border"
                                            style={{ maxHeight: 80, objectFit: "cover" }}
                                          />
                                        )}
                                        {template.header_type === "VIDEO" && template.url_midia && (
                                          <video
                                            src={template.url_midia}
                                            controls
                                            className="rounded-2 border"
                                            style={{ maxHeight: 120 }}
                                          />
                                        )}
                                        {(template.header_type === "IMAGE" || template.header_type === "VIDEO") && !template.url_midia && (
                                          <span className="fs-12 text-muted fst-italic">
                                            (mídia hospedada na Meta — sem cópia no bucket)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Body */}
                                  <div className="bg-light p-3 rounded-3 border">
                                    <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">Corpo da Mensagem</span>
                                    <p
                                      className="fs-13 mb-0"
                                      style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
                                      dangerouslySetInnerHTML={{ __html: renderBodyPreview(template.body) }}
                                    />
                                  </div>

                                  {/* Footer */}
                                  {template.footer && (
                                    <div className="bg-light p-3 rounded-3 border">
                                      <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">Rodapé</span>
                                      <span className="fs-12 text-muted">{template.footer}</span>
                                    </div>
                                  )}

                                  {/* Buttons */}
                                  {template.buttons.length > 0 && (
                                    <div className="bg-light p-3 rounded-3 border">
                                      <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">
                                        Botões ({template.buttons.length})
                                      </span>
                                      <div className="d-flex flex-column gap-2">
                                        {template.buttons.map((btn, idx) => (
                                          <div key={idx} className="d-flex align-items-center gap-2 p-2 bg-white rounded-2 border">
                                            <i
                                              className={`ti ${
                                                btn.type === "QUICK_REPLY"
                                                  ? "ti-message-reply"
                                                  : btn.type === "URL"
                                                  ? "ti-external-link"
                                                  : "ti-phone"
                                              } text-muted fs-14`}
                                            ></i>
                                            <div>
                                              <span className="fs-12 fw-medium">{btn.text}</span>
                                              <span className="fs-10 text-muted d-block">
                                                {btn.type === "QUICK_REPLY"
                                                  ? "Resposta Rápida"
                                                  : btn.type === "URL"
                                                  ? btn.url
                                                  : btn.phone_number}
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Variáveis */}
                                  {variables.length > 0 && (
                                    <div className="bg-soft-primary p-3 rounded-3 border border-primary-subtle">
                                      <span className="fs-11 fw-bold text-uppercase text-primary d-block mb-2">
                                        <i className="ti ti-variable me-1"></i>Variáveis Detectadas
                                      </span>
                                      <div className="d-flex gap-2 flex-wrap">
                                        {variables.map((v, idx) => (
                                          <span key={idx} className="badge bg-primary text-white fs-11 rounded-pill px-2">
                                            {v}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Preview WhatsApp */}
                              <div className="col-12 col-md-5 mt-3 mt-md-0">
                                <div className="d-flex flex-column align-items-center">
                                  <span className="fs-11 fw-bold text-uppercase text-muted mb-2">Preview</span>
                                  <div
                                    className="rounded-4 border shadow-sm overflow-hidden"
                                    style={{ width: "100%", maxWidth: "320px", margin: "0 auto", background: "#e5ddd5" }}
                                  >
                                    {/* WhatsApp Header Bar */}
                                    <div
                                      className="d-flex align-items-center gap-2 px-3 py-2"
                                      style={{ background: "#075e54" }}
                                    >
                                      <i className="ti ti-arrow-left text-white fs-16"></i>
                                      <div
                                        className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                                        style={{ width: "32px", height: "32px" }}
                                      >
                                        <i className="ti ti-building text-muted fs-14"></i>
                                      </div>
                                      <div>
                                        <span className="text-white fs-13 fw-medium d-block" style={{ lineHeight: "1.2" }}>
                                          Sua Empresa
                                        </span>
                                        <span className="text-white-50 fs-10">online</span>
                                      </div>
                                    </div>

                                    {/* Chat Area */}
                                    <div className="p-3" style={{ minHeight: "300px" }}>
                                      <div
                                        className="rounded-3 p-3 mb-2 position-relative"
                                        style={{
                                          background: "#dcf8c6",
                                          maxWidth: "95%",
                                          marginLeft: "auto",
                                          boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                                        }}
                                      >
                                        {template.header_type === "TEXT" && template.header_text && (
                                          <p className="fw-bold fs-13 mb-1" style={{ color: "#303030" }}>
                                            {template.header_text}
                                          </p>
                                        )}
                                        {template.header_type === "IMAGE" && template.url_midia && (
                                          <img
                                            src={template.url_midia}
                                            alt="header"
                                            className="rounded-2 mb-2"
                                            style={{ width: "100%", maxHeight: 180, objectFit: "cover" }}
                                          />
                                        )}
                                        {template.header_type === "VIDEO" && template.url_midia && (
                                          <video
                                            src={template.url_midia}
                                            controls
                                            className="rounded-2 mb-2"
                                            style={{ width: "100%", maxHeight: 200 }}
                                          />
                                        )}
                                        {(template.header_type === "IMAGE" || template.header_type === "VIDEO") && !template.url_midia && (
                                          <div
                                            className="rounded-2 mb-2 d-flex align-items-center justify-content-center bg-light text-muted"
                                            style={{ width: "100%", height: 120 }}
                                          >
                                            <i className={`ti ${template.header_type === "IMAGE" ? "ti-photo" : "ti-video"} fs-32`}></i>
                                          </div>
                                        )}
                                        <p
                                          className="fs-12 mb-1"
                                          style={{ color: "#303030", whiteSpace: "pre-wrap", lineHeight: "1.5" }}
                                        >
                                          {template.body.replace(/\{\{(\d+)\}\}/g, "[valor $1]")}
                                        </p>
                                        {template.footer && (
                                          <p className="fs-10 text-muted mb-0 mt-1">{template.footer}</p>
                                        )}
                                        <div className="text-end mt-1">
                                          <span className="fs-9 text-muted">
                                            {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                            <i className="ti ti-checks text-info ms-1 fs-10"></i>
                                          </span>
                                        </div>
                                      </div>

                                      {template.buttons.length > 0 && (
                                        <div className="d-flex flex-column gap-1" style={{ maxWidth: "95%", marginLeft: "auto" }}>
                                          {template.buttons.map((btn, idx) => (
                                            <div
                                              key={idx}
                                              className="text-center rounded-2 py-2 px-3"
                                              style={{
                                                background: "#ffffff",
                                                boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                                              }}
                                            >
                                              <span className="fs-12 text-primary fw-medium">
                                                {btn.type === "URL" && <i className="ti ti-external-link me-1 fs-11"></i>}
                                                {btn.type === "PHONE_NUMBER" && <i className="ti ti-phone me-1 fs-11"></i>}
                                                {btn.text || "Botão"}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </div>
              </div>
            </Tab.Container>
          </div>
        ))}
      </div>

      {/* ─── Modal de Criação ─────────────────────────────────── */}
      <Modal show={showModal} onHide={closeModal} centered size="lg" className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">
            <i className="ti ti-brand-whatsapp text-success me-2"></i>
            Novo Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger" className="fs-12 py-2" dismissible onClose={() => setSaveError("")}>{saveError}</Alert>}
          {saveSuccess && <Alert variant="success" className="fs-12 py-2">{saveSuccess}</Alert>}
          {/* Resultado por WABA — o que entrou e o que não, com o motivo da Meta */}
          {saveResults.length > 1 && (
            <div className="border rounded-3 mb-3">
              {saveResults.map((r) => (
                <div
                  key={r.waba_id}
                  className="d-flex align-items-start gap-2 px-3 py-2 border-bottom fs-12"
                >
                  <i
                    className={`ti ${r.ok ? "ti-circle-check text-success" : "ti-circle-x text-danger"} fs-15`}
                    style={{ marginTop: 1 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <span className="fw-medium">{wabaLabel(r.waba_id)}</span>
                    {r.ok ? (
                      <span className="text-muted ms-2">
                        {r.status === "APPROVED" ? "aprovado" : "enviado para aprovação"}
                        {r.category && r.category !== formCategory && (
                          <span className="badge bg-soft-info text-info fs-10 rounded-pill px-2 ms-2">
                            reclassificado: {r.category}
                          </span>
                        )}
                      </span>
                    ) : (
                      <div className="text-danger" style={{ wordBreak: "break-word" }}>{r.error}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="row g-3">
            {/* Nome do Template */}
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">NOME DO TEMPLATE</Form.Label>
                <Form.Control
                  className="border-2 shadow-none"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                  placeholder="ex: confirmacao_pedido, boas_vindas_lead"
                />
                <Form.Text className="text-muted fs-11">Apenas letras minúsculas, números e underscores.</Form.Text>
              </Form.Group>
            </div>

            {/* Categoria */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">CATEGORIA</Form.Label>
                <div className="d-flex gap-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      className={`btn btn-sm flex-fill d-flex align-items-center justify-content-center gap-1 rounded-pill ${
                        formCategory === cat.value ? `btn-${cat.color}` : `btn-outline-${cat.color}`
                      }`}
                      onClick={() => setFormCategory(cat.value)}
                    >
                      <i className={`ti ${cat.icon} fs-14`}></i>
                      <span className="fs-11">{cat.label}</span>
                    </button>
                  ))}
                </div>
                {formCategory === "MARKETING" ? (
                  <Form.Text className="text-danger fs-11">
                    <i className="ti ti-alert-triangle me-1"></i>
                    Prefira <strong>Utilidade</strong>: a Meta revisa mais rápido e reclassifica para
                    Marketing sozinha quando é o caso. Depois de aprovado, a categoria não muda mais.
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted fs-11">
                    A Meta pode reclassificar para Marketing na revisão — é o esperado.
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            {/* WABAs de destino — só quando o grupo tem mais de uma conta */}
            {multiWaba && (
              <div className="col-12">
                <Form.Group>
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label
                      className={`fs-12 fw-bold mb-0 ${
                        precisaEscolherWaba ? "text-danger" : "text-muted"
                      }`}
                    >
                      CRIAR NAS CONTAS (WABA) <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 fs-11 text-decoration-none"
                        onClick={() => setFormWabas(wabas.map((w) => w.waba_id))}
                      >
                        Todas
                      </button>
                      <span className="text-muted fs-11">|</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 fs-11 text-decoration-none text-muted"
                        onClick={() => setFormWabas([])}
                      >
                        Nenhuma
                      </button>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {wabas.map((w) => {
                      const marcada = formWabas.includes(w.waba_id);
                      return (
                        <button
                          key={w.waba_id}
                          type="button"
                          className={`btn btn-sm d-flex align-items-center gap-1 rounded-pill px-3 ${
                            marcada ? "btn-primary" : "btn-outline-secondary"
                          }`}
                          onClick={() =>
                            setFormWabas((prev) =>
                              prev.includes(w.waba_id)
                                ? prev.filter((x) => x !== w.waba_id)
                                : [...prev, w.waba_id]
                            )
                          }
                          title={`WABA ${w.waba_id}`}
                        >
                          <i className={`ti ${marcada ? "ti-check" : "ti-plus"} fs-13`}></i>
                          <span className="fs-11">{wabaLabel(w.waba_id)}</span>
                        </button>
                      );
                    })}
                  </div>
                  {precisaEscolherWaba ? (
                    <Form.Text className="text-danger fs-11 fw-medium">
                      <i className="ti ti-alert-circle me-1"></i>
                      Escolha obrigatória: este login tem {wabas.length} contas. Marque em qual(is)
                      o template deve ser criado.
                    </Form.Text>
                  ) : (
                    <Form.Text className="text-muted fs-11">
                      O template é criado uma vez em cada conta marcada e registrado para todos os
                      números dela. Falha numa conta não impede as outras.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            )}

            {/* Idioma */}
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">IDIOMA</Form.Label>
                <Form.Select className="border-2 shadow-none" value={formLanguage} onChange={(e) => setFormLanguage(e.target.value)}>
                  {LANGUAGES.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {/* Tipo de Header */}
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">HEADER (CABEÇALHO)</Form.Label>
                <div className="d-flex gap-2 flex-wrap">
                  {HEADER_TYPES.map((ht) => (
                    <button
                      key={ht.value}
                      type="button"
                      className={`btn btn-sm d-flex align-items-center justify-content-center gap-1 rounded-3 px-4 ${
                        formHeaderType === ht.value ? "btn-dark" : "btn-outline-secondary"
                      }`}
                      onClick={() => setFormHeaderType(ht.value)}
                    >
                      <i className={`ti ${ht.icon} fs-14`}></i>
                      <span className="fs-11">{ht.label}</span>
                    </button>
                  ))}
                </div>
              </Form.Group>
            </div>

            {/* Upload de Mídia (IMAGE / VIDEO) */}
            {(formHeaderType === "IMAGE" || formHeaderType === "VIDEO") && (
              <div className="col-12 animate__animated animate__fadeInDown">
                <Form.Group>
                  <Form.Label className="fs-12 fw-bold text-muted mb-1">
                    {formHeaderType === "IMAGE" ? "IMAGEM DO HEADER" : "VÍDEO DO HEADER"}
                  </Form.Label>
                  <div className="d-flex align-items-start gap-3">
                    <div className="flex-grow-1">
                      <Form.Control
                        className="border-2 shadow-none"
                        type="file"
                        accept={formHeaderType === "IMAGE" ? "image/*" : "video/*"}
                        onChange={handleHeaderFileChange}
                      />
                      <Form.Text className="text-muted fs-11 d-block mt-1">
                        Máx 16MB. A mídia vai para o bucket Supabase e o template é criado na Meta via Resumable Upload.
                        No envio, o backend baixa do bucket e envia como media_id.
                      </Form.Text>
                    </div>
                    {formHeaderPreviewUrl && (
                      <div className="border rounded-3 overflow-hidden bg-light" style={{ width: 96, height: 96, flexShrink: 0 }}>
                        {formHeaderType === "IMAGE" ? (
                          <img
                            src={formHeaderPreviewUrl}
                            alt="preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <video
                            src={formHeaderPreviewUrl}
                            muted
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
            )}

            {/* Header Text (condicional) */}
            {formHeaderType === "TEXT" && (
              <div className="col-12 animate__animated animate__fadeInDown">
                <Form.Group>
                  <Form.Label className="fs-12 fw-bold text-muted mb-1">TEXTO DO HEADER</Form.Label>
                  <Form.Control
                    className="border-2 shadow-none"
                    type="text"
                    value={formHeaderText}
                    onChange={(e) => setFormHeaderText(e.target.value)}
                    placeholder="Ex: Confirmação de Pedido #{{1}}"
                    maxLength={60}
                  />
                  <Form.Text className="text-muted fs-11">{formHeaderText.length}/60 caracteres. Use {"{{1}}"} para variável.</Form.Text>
                </Form.Group>
                {headerHasVar && (
                  <Form.Group className="mt-2">
                    <Form.Label className="fs-11 text-primary fw-bold mb-1">
                      <i className="ti ti-variable me-1"></i>Exemplo para {"{{1}}"} do header
                    </Form.Label>
                    <Form.Control
                      className="border-2 shadow-none fs-12"
                      type="text"
                      value={formHeaderExample}
                      onChange={(e) => setFormHeaderExample(e.target.value)}
                      placeholder="Ex: #12345"
                    />
                  </Form.Group>
                )}
              </div>
            )}

            {/* Body */}
            <div className="col-12">
              <Form.Group>
                <div className="d-flex align-items-center justify-content-between mb-1 gap-2 flex-wrap">
                  <Form.Label className="fs-12 fw-bold text-muted mb-0">
                    CORPO DA MENSAGEM <span className="text-danger">*</span>
                  </Form.Label>
                  <button
                    type="button"
                    className="btn btn-sm btn-ia-utility rounded-pill d-flex align-items-center gap-1 px-3"
                    onClick={ajustarParaUtility}
                    disabled={!formBody.trim() || iaLoading}
                    title="A IA reescreve o texto para ser aprovado e mantido como Utilidade pela Meta"
                  >
                    {iaLoading ? (
                      <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                    ) : (
                      <i className="ti ti-sparkles fs-14"></i>
                    )}
                    <span className="badge-ia">IA</span>
                    <span className="fs-11 fw-semibold">{iaLoading ? "Ajustando..." : "Ajustar para utility"}</span>
                  </button>
                </div>
                <Form.Control
                  as="textarea"
                  className={`border-2 shadow-none ${iaLoading ? "ia-textarea-loading" : ""}`}
                  rows={5}
                  value={formBody}
                  readOnly={iaLoading}
                  onChange={(e) => setFormBody(e.target.value)}
                  placeholder={`Olá {{1}}, tudo bem?\n\nSeu pedido {{2}} foi confirmado com sucesso!\n\nValor: R$ {{3}}\nPrevisão de entrega: {{4}}`}
                  maxLength={1024}
                />
                <div className="d-flex justify-content-between mt-1">
                  <Form.Text className="text-muted fs-11">
                    Use {"{{1}}"}, {"{{2}}"}, etc. para variáveis dinâmicas.
                  </Form.Text>
                  <Form.Text className="text-muted fs-11">{formBody.length}/1024</Form.Text>
                </div>

                {iaErro && (
                  <Alert variant="danger" className="fs-12 py-2 mt-2 mb-0" dismissible onClose={() => setIaErro("")}>
                    <i className="ti ti-alert-circle me-1"></i>{iaErro}
                  </Alert>
                )}

                {iaResultado && (
                  <div className={`ia-painel mt-2 animate__animated animate__fadeInDown animate__faster ${iaResultado.convertivel ? "ia-ok" : "ia-nao"}`}>
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="fs-12 fw-bold d-flex align-items-center gap-1">
                        <i className={`ti ${iaResultado.convertivel ? "ti-circle-check" : "ti-ban"} fs-16`}></i>
                        {iaResultado.convertivel
                          ? "Texto ajustado para Utilidade"
                          : "Não dá para virar Utilidade, o texto não foi alterado"}
                        {iaResultado.convertivel && iaResultado.caso_de_uso && (
                          <span className="badge rounded-pill bg-white text-success border fs-10 ms-1">{iaResultado.caso_de_uso}</span>
                        )}
                      </div>
                      <div className="d-flex gap-1 flex-shrink-0">
                        {iaSnapshot && (
                          <button type="button" className="btn btn-sm btn-light border rounded-pill px-2 fs-11" onClick={desfazerAjusteIa}>
                            <i className="ti ti-arrow-back-up me-1"></i>Desfazer
                          </button>
                        )}
                        <button type="button" className="btn btn-sm btn-link text-muted p-0 px-1" onClick={() => setIaResultado(null)} title="Fechar">
                          <i className="ti ti-x fs-14"></i>
                        </button>
                      </div>
                    </div>

                    {iaResultado.veredito && <div className="fs-11 mt-1 opacity-75">{iaResultado.veredito}</div>}

                    {iaResultado.diagnostico.length > 0 && (
                      <div className="mt-2">
                        <span className="fs-10 fw-bold text-uppercase opacity-75">
                          {iaResultado.convertivel ? "O que foi tirado" : "O que deixa como Marketing"}
                        </span>
                        <ul className="fs-11 mb-0 ps-3 mt-1">
                          {iaResultado.diagnostico.map((d, i) => (
                            <li key={i}>
                              <span className="ia-trecho">{d.trecho}</span> <span className="opacity-75">· {d.gatilho}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {iaResultado.variaveis_novas && (
                      <div className="fs-11 mt-2">
                        <i className="ti ti-variable me-1"></i><strong>Variável nova:</strong> {iaResultado.variaveis_novas}
                      </div>
                    )}
                    {iaResultado.avisos.map((a, i) => (
                      <div key={i} className="fs-11 mt-1 text-danger">
                        <i className="ti ti-alert-triangle me-1"></i>{a}
                      </div>
                    ))}
                    {iaResultado.convertivel && iaResultado.risco && (
                      <div className="fs-11 mt-2 opacity-75"><i className="ti ti-shield-half me-1"></i>{iaResultado.risco}</div>
                    )}
                    {iaResultado.alternativa && (
                      <div className="fs-11 mt-2"><i className="ti ti-arrows-split me-1"></i><strong>Sugestão:</strong> {iaResultado.alternativa}</div>
                    )}
                    {iaResultado.convertivel && (
                      <div className="fs-10 mt-2 opacity-75">Revise o texto e os exemplos das variáveis antes de criar.</div>
                    )}
                  </div>
                )}
              </Form.Group>
            </div>

            {/* Variáveis do Body — DOIS destinos bem separados: exemplo p/ Meta vs nome p/ banco */}
            {bodyVarsInForm.length > 0 && (
              <div className="col-12">
                <div className="bg-soft-primary p-3 rounded-3 border border-primary-subtle">
                  <Form.Label className="fs-12 fw-bold text-primary mb-1">
                    <i className="ti ti-variable me-1"></i>VARIÁVEIS DO CORPO
                  </Form.Label>
                  {/* Legenda: deixa explícito que cada variável tem DOIS destinos diferentes */}
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge fs-11 rounded-pill px-2 py-1" style={{ background: "rgba(255,193,7,0.15)", color: "#b8860b", border: "1px solid rgba(255,193,7,0.45)" }}>
                      <i className="ti ti-brand-meta me-1"></i>Exemplo → só para a META (aprovação)
                    </span>
                    <span className="badge fs-11 rounded-pill px-2 py-1" style={{ background: "rgba(25,135,84,0.12)", color: "#198754", border: "1px solid rgba(25,135,84,0.40)" }}>
                      <i className="ti ti-database me-1"></i>Nome → salvo no BANCO (texto_template)
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {bodyVarsInForm.map((v, idx) => {
                      const n = v.replace(/\D/g, ""); // número da variável ("1","2"...)
                      const mode = formVarModes[n] || "card";
                      const usaNomeContato = (formVarNames[n] || "") === "nome_lead";
                      const usaNomeAgente = (formVarNames[n] || "") === "nome_user";
                      const usaVarSistema = usaNomeContato || usaNomeAgente;
                      // Os dois atalhos gravam uma variável de sistema na MESMA posição —
                      // ligar um desliga o outro (uma variável não é lead e atendente ao mesmo tempo).
                      const ligarVarSistema = (valor: string, exemplo: string) => (e: any) => {
                        setFormVarModes((m) => ({ ...m, [n]: "card" }));
                        setFormVarNames((nm) => ({ ...nm, [n]: e.target.checked ? valor : "" }));
                        if (e.target.checked) {
                          // Exemplo p/ Meta é obrigatório; preenche um nome de amostra se estiver vazio.
                          setFormBodyExamples((prev) => {
                            const nx = [...prev];
                            if (!nx[idx] || !nx[idx].trim()) nx[idx] = exemplo;
                            return nx;
                          });
                        }
                      };
                      return (
                        <div key={idx} className="bg-white rounded-2 border overflow-hidden">
                          {/* Cabeçalho: número da variável + atalhos "nome do contato" / "nome do atendente" */}
                          <div className="d-flex align-items-center justify-content-between px-2 py-2 border-bottom bg-light gap-2 flex-wrap">
                            <span className="badge bg-primary text-white fs-11 rounded-pill px-2" style={{ minWidth: 42 }}>{v}</span>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <Form.Check
                                type="switch"
                                id={`switch-nome-contato-${n}`}
                                className="fs-12 fw-semibold mb-0"
                                checked={usaNomeContato}
                                label={<span><i className="ti ti-user me-1"></i>Usar nome do contato</span>}
                                onChange={ligarVarSistema("nome_lead", "João")}
                              />
                              <Form.Check
                                type="switch"
                                id={`switch-nome-agente-${n}`}
                                className="fs-12 fw-semibold mb-0"
                                checked={usaNomeAgente}
                                label={<span><i className="ti ti-headset me-1"></i>Usar nome do atendente</span>}
                                onChange={ligarVarSistema("nome_user", "Maria")}
                              />
                            </div>
                          </div>

                          <div className="p-2 d-flex flex-column gap-2">
                            {/* BLOCO META (âmbar) — só vai para a aprovação, não é salvo */}
                            <div className="rounded-2 p-2" style={{ background: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.35)" }}>
                              <span className="fs-10 fw-bold text-uppercase d-block mb-1" style={{ color: "#b8860b" }}>
                                <i className="ti ti-brand-meta me-1"></i>Exemplo para a Meta
                              </span>
                              <Form.Control
                                className="border shadow-none fs-12"
                                type="text"
                                value={formBodyExamples[idx] || ""}
                                onChange={(e) => {
                                  const newExamples = [...formBodyExamples];
                                  newExamples[idx] = e.target.value;
                                  setFormBodyExamples(newExamples);
                                }}
                                placeholder="ex: João, PED-123, 99.90..."
                              />
                              <span className="fs-10 text-muted d-block mt-1">Usado só na aprovação da Meta. <strong>Não</strong> é salvo no banco.</span>
                            </div>

                            {/* BLOCO BANCO (verde) — o que substitui {{N}} em texto_template */}
                            <div className="rounded-2 p-2" style={{ background: "rgba(25,135,84,0.07)", border: "1px solid rgba(25,135,84,0.30)" }}>
                              <span className="fs-10 fw-bold text-uppercase d-block mb-1 text-success">
                                <i className="ti ti-database me-1"></i>Nome no sistema (banco)
                              </span>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Form.Select
                                  className="border shadow-none fs-12"
                                  style={{ maxWidth: 240 }}
                                  disabled={usaVarSistema}
                                  value={usaVarSistema ? (formVarNames[n] || "") : (mode === "manual" ? "__manual__" : (formVarNames[n] || ""))}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "__manual__") {
                                      setFormVarModes((m) => ({ ...m, [n]: "manual" }));
                                    } else {
                                      setFormVarModes((m) => ({ ...m, [n]: "card" }));
                                      setFormVarNames((nm) => ({ ...nm, [n]: val }));
                                    }
                                  }}
                                >
                                  <option value="">— manter {`{{${n}}}`} —</option>
                                  {OPTGROUP_SISTEMA}
                                  {Object.entries(groupedCardFields).map(([grp, labels]) => (
                                    <optgroup key={grp} label={grp}>
                                      {labels.map((lbl) => (
                                        <option key={`${grp}-${lbl}`} value={lbl}>{lbl}</option>
                                      ))}
                                    </optgroup>
                                  ))}
                                  <option value="__manual__">✏️ Digitar manualmente</option>
                                </Form.Select>
                                {usaVarSistema ? (
                                  <span className="badge bg-success text-white fs-11 rounded-pill px-2">
                                    <i className="ti ti-check me-1"></i>{formVarNames[n]}
                                  </span>
                                ) : mode === "manual" && (
                                  <Form.Control
                                    className="border shadow-none fs-12"
                                    style={{ maxWidth: 220 }}
                                    type="text"
                                    value={formVarNames[n] || ""}
                                    onChange={(e) => setFormVarNames((nm) => ({ ...nm, [n]: e.target.value }))}
                                    placeholder="nome no sistema (ex: nome_lead)"
                                  />
                                )}
                              </div>
                              <span className="fs-10 text-muted d-block mt-1">Substitui {`{{${n}}}`} em <code>texto_template</code> (cards/automação). Em branco = mantém o número.</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Preview do texto_template que será salvo no banco */}
                  <div className="bg-light rounded-2 border p-2 mt-3">
                    <span className="fs-10 fw-bold text-uppercase text-muted d-block mb-1">
                      <i className="ti ti-eye me-1"></i>Será salvo no banco (texto_template)
                    </span>
                    <span className="fs-12" style={{ whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
                      {buildTextoTemplate(formBody, formVarNames)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">RODAPÉ (OPCIONAL)</Form.Label>
                <Form.Control
                  className="border-2 shadow-none"
                  type="text"
                  value={formFooter}
                  onChange={(e) => setFormFooter(e.target.value)}
                  placeholder="Ex: Responda SAIR para cancelar"
                  maxLength={60}
                />
                <Form.Text className="text-muted fs-11">{formFooter.length}/60 caracteres</Form.Text>
              </Form.Group>
            </div>

            {/* Buttons */}
            <div className="col-12">
              <div className="bg-light p-3 rounded-3 border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="fs-12 fw-bold text-muted mb-0">BOTÕES ({formButtons.length}/3)</Form.Label>
                  {formButtons.length < 3 && (
                    <button type="button" className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={addButton}>
                      <i className="ti ti-plus fs-12 me-1"></i>Adicionar
                    </button>
                  )}
                </div>
                {formButtons.length === 0 ? (
                  <p className="text-muted fs-11 mb-0">Nenhum botão adicionado. Botões são opcionais.</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {formButtons.map((btn, idx) => (
                      <div key={idx} className="bg-white p-2 rounded-2 border">
                        <div className="row g-2 align-items-center">
                          <div className="col-md-3">
                            <Form.Select
                              className="border shadow-none fs-12"
                              value={btn.type}
                              onChange={(e) => updateButton(idx, { type: e.target.value as TemplateButton["type"] })}
                            >
                              <option value="QUICK_REPLY">Resposta Rápida</option>
                              <option value="URL">Link (URL)</option>
                              <option value="PHONE_NUMBER">Telefone</option>
                            </Form.Select>
                          </div>
                          <div className="col">
                            <Form.Control
                              className="border shadow-none fs-12"
                              type="text"
                              value={btn.text}
                              onChange={(e) => updateButton(idx, { text: e.target.value })}
                              placeholder="Texto do botão (max 25)"
                              maxLength={25}
                            />
                          </div>
                          {btn.type === "URL" && (
                            <div className="col">
                              <Form.Control
                                className="border shadow-none fs-12"
                                type="text"
                                value={btn.url || ""}
                                onChange={(e) => updateButton(idx, { url: e.target.value })}
                                placeholder="https://..."
                              />
                            </div>
                          )}
                          {btn.type === "PHONE_NUMBER" && (
                            <div className="col">
                              <Form.Control
                                className="border shadow-none fs-12"
                                type="text"
                                value={btn.phone_number || ""}
                                onChange={(e) => updateButton(idx, { phone_number: e.target.value })}
                                placeholder="+5511999999999"
                              />
                            </div>
                          )}
                          <div className="col-auto">
                            <button type="button" className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => removeButton(idx)}>
                              <i className="ti ti-x fs-12"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            className="rounded-pill px-4 fw-bold shadow-sm"
            onClick={handleCreateTemplate}
            disabled={!formName.trim() || !formBody.trim() || saving || precisaEscolherWaba}
            title={
              precisaEscolherWaba
                ? "Escolha em qual conta (WABA) o template será criado"
                : undefined
            }
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enviando para Meta...
              </>
            ) : (
              "Criar Template"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Modal de Confirmação de Exclusão ────────────────── */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered size="sm" className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold fs-16">Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <i className="ti ti-alert-triangle text-danger fs-40 mb-2"></i>
          <p className="fs-13 mb-1">
            Tem certeza que deseja excluir o template <strong>{templateToDelete?.name}</strong>?
          </p>
          <p className="text-muted fs-12">O template será removido da Meta. O nome não poderá ser reutilizado por 30 dias.</p>
          {/* Erro da Meta em vez de fechar calado — o modal fica aberto para o operador ler. */}
          {deleteError && (
            <div className="alert alert-danger text-start fs-12 mb-0 py-2 px-3">
              <i className="ti ti-alert-circle me-1" />
              {deleteError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button variant="light" className="rounded-pill px-4" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button variant="danger" className="rounded-pill px-4 fw-bold" onClick={handleDeleteTemplate} disabled={deleting}>
            {deleting ? <Spinner animation="border" size="sm" /> : "Excluir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Modal de Mapeamento de Variáveis ─────────────────── */}
      <Modal show={showVarModal} onHide={closeVarModal} centered size="lg" className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold fs-16">
            <i className="ti ti-variable text-primary me-2"></i>
            {varTarget?.in_db ? "Editar variáveis" : "Registrar no sistema"}
            {varTarget && <span className="text-muted fw-normal fs-13 ms-2">· {varTarget.name}</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {varError && <Alert variant="danger" className="fs-12 py-2" dismissible onClose={() => setVarError("")}>{varError}</Alert>}

          <p className="text-muted fs-12 mb-3">
            Para cada variável <code>{"{{N}}"}</code> da Meta, escolha uma <strong>variável de sistema</strong>
            {" "}(<code>nome_lead</code> = nome do contato, <code>nome_user</code> = nome do atendente), um
            {" "}<strong>campo de card</strong> existente ou digite um nome manualmente. O nome escolhido é salvo no
            lugar da variável em <code>texto_template</code> (usado pelas cadências/automação).
          </p>

          {/* Corpo do template (referência) */}
          {varTarget && (
            <div className="bg-light p-3 rounded-3 border mb-3">
              <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">Corpo (Meta)</span>
              <p
                className="fs-12 mb-0"
                style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
                dangerouslySetInnerHTML={{ __html: renderBodyPreview(varTarget.body) }}
              />
            </div>
          )}

          {/* Linhas de variáveis */}
          {varTargetNums.length === 0 ? (
            <Alert variant="info" className="fs-12 py-2 mb-0">
              Este template não tem variáveis. Clique em salvar para registrá-lo no sistema.
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {varTargetNums.map((n) => {
                const mode = varModes[n] || "card";
                return (
                  <div key={n} className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary text-white fs-11 rounded-pill px-2" style={{ minWidth: 46 }}>
                      {`{{${n}}}`}
                    </span>
                    <i className="ti ti-arrow-right text-muted fs-14"></i>
                    <Form.Select
                      className="border shadow-none fs-12"
                      style={{ maxWidth: mode === "manual" ? 220 : undefined }}
                      value={mode === "manual" ? "__manual__" : (varNames[n] || "")}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__manual__") {
                          setVarModes((m) => ({ ...m, [n]: "manual" }));
                        } else {
                          setVarModes((m) => ({ ...m, [n]: "card" }));
                          setVarNames((nm) => ({ ...nm, [n]: v }));
                        }
                      }}
                    >
                      <option value="">— selecione um campo —</option>
                      {OPTGROUP_SISTEMA}
                      {Object.entries(groupedCardFields).map(([grp, labels]) => (
                        <optgroup key={grp} label={grp}>
                          {labels.map((lbl) => (
                            <option key={`${grp}-${lbl}`} value={lbl}>{lbl}</option>
                          ))}
                        </optgroup>
                      ))}
                      <option value="__manual__">✏️ Digitar manualmente</option>
                    </Form.Select>
                    {mode === "manual" && (
                      <Form.Control
                        className="border shadow-none fs-12"
                        type="text"
                        value={varNames[n] || ""}
                        onChange={(e) => setVarNames((nm) => ({ ...nm, [n]: e.target.value }))}
                        placeholder="nome da variável (ex: nome_lead)"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Preview do texto_template resultante */}
          {varTarget && varTargetNums.length > 0 && (
            <div className="bg-soft-primary p-3 rounded-3 border border-primary-subtle mt-3">
              <span className="fs-11 fw-bold text-uppercase text-primary d-block mb-2">
                <i className="ti ti-eye me-1"></i>Será salvo como
              </span>
              <p className="fs-12 mb-0" style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                {buildTextoTemplate(varTarget.body, varNames)}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={closeVarModal} disabled={savingVars}>
            Cancelar
          </Button>
          <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleSaveVars} disabled={savingVars}>
            {savingVars ? (
              <><Spinner animation="border" size="sm" className="me-2" />Salvando...</>
            ) : (
              varTarget?.in_db ? "Salvar variáveis" : "Registrar no sistema"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Modal de Classificação ───────────────────────────── */}
      <Modal show={showClassifModal} onHide={() => setShowClassifModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold fs-16">
            <i className="ti ti-tag text-primary me-2"></i>
            Classificar template
            {classifTarget && <span className="text-muted fw-normal fs-13 ms-2">· {classifTarget.name}</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {classifTarget && (
            <div className="bg-light p-3 rounded-3 border mb-3" style={{ maxHeight: 260, overflowY: "auto" }}>
              <span className="fs-11 fw-bold text-uppercase text-muted d-block mb-2">Texto do template</span>
              {classifTarget.header_type === "TEXT" && classifTarget.header_text && (
                <p className="fw-bold fs-13 mb-2" style={{ color: "#303030" }}>{classifTarget.header_text}</p>
              )}
              <p className="fs-13 mb-0" style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                {classifTarget.body || "(sem corpo)"}
              </p>
              {classifTarget.footer && <p className="fs-11 text-muted mb-0 mt-2">{classifTarget.footer}</p>}
            </div>
          )}
          <Form.Group>
            <Form.Label className="fs-12 fw-bold text-muted mb-2">CLASSIFICAÇÃO</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className={`btn btn-sm rounded-pill px-3 ${!classifValue ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setClassifValue("")}>
                Sem classificação
              </button>
              {CLASSIFICACOES.map((c) => (
                <button key={c} type="button" className={`btn btn-sm rounded-pill px-3 ${classifValue === c ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setClassifValue(c)}>
                  {c}
                </button>
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={() => setShowClassifModal(false)} disabled={savingClassif}>Cancelar</Button>
          <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={saveClassif} disabled={savingClassif}>
            {savingClassif ? (<><Spinner animation="border" size="sm" className="me-2" />Salvando...</>) : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WhatsAppTemplates;
